import {useEffect, useRef} from "react";
import {Controller, useFormContext, useFormState} from "react-hook-form";
import {Switch} from "@mui/material";
import {Box, Colors, formatHtml, HtmlCodeEditor, Text} from "@bounce/shared/ui";
import {Button} from "../../ui/components";
import {FormValues} from "./templateFormSchema";

type CollapsibleHtmlFieldProps = {
  name: "body" | "landing_page_body";
  label: string;
  show: boolean;
  onToggle: (show: boolean) => void;
  minRows: number;
  placeholder: string;
  error?: boolean;
};

const CollapsibleHtmlField = ({name, label, show, onToggle, minRows, placeholder, error}: CollapsibleHtmlFieldProps) => {
  const {control, getValues, setValue} = useFormContext<FormValues>();
  const {dirtyFields} = useFormState({control, name});
  const isFieldDirty = !!dirtyFields[name];
  const hasAutoFormatted = useRef(false);

  const onFormat = () => {
    const current = getValues(name);
    if (!current?.trim()) return;
    setValue(name, formatHtml(current), {shouldDirty: true});
  };

  useEffect(() => {
    if (!show || hasAutoFormatted.current) return;
    hasAutoFormatted.current = true;
    const current = getValues(name);
    if (!current?.trim()) return;
    const formatted = formatHtml(current);
    if (formatted !== current) setValue(name, formatted, {shouldDirty: false});
  }, [show, name, getValues, setValue]);

  return (
    <>
      <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5}}>
        <Box sx={{display: "flex", alignItems: "baseline", gap: 1}}>
          <Text size={13} sx={{color: Colors.grey._700}}>
            {label}
          </Text>
          {error && (
            <Text size={12} sx={{color: Colors.red._900}}>
              Required
            </Text>
          )}
        </Box>
        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
          {show && (
            <Button buttonType="outlined-primary" size="small" onClick={onFormat} disabled={!isFieldDirty}>
              Format
            </Button>
          )}
          <Switch size="small" checked={show} onChange={(e) => onToggle(e.target.checked)} />
        </Box>
      </Box>
      {show && (
        <Controller
          name={name}
          control={control}
          render={({field: {ref, onChange, ...field}, fieldState}) => (
            <HtmlCodeEditor
              value={field.value}
              onChange={onChange}
              onBlur={field.onBlur}
              inputRef={ref}
              minRows={minRows}
              placeholder={placeholder}
              error={!!fieldState.error}
            />
          )}
        />
      )}
    </>
  );
};

export default CollapsibleHtmlField;
